// src/app/core/services/api.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@env/environment';

export type ParamsLike = HttpParams | { [param: string]: string | number | boolean | Array<string | number | boolean> | undefined };

export interface RequestOptions {
  params?: ParamsLike;
  headers?: HttpHeaders;
}

@Injectable({ providedIn: 'root' })
export class Api {
  private baseUrl = environment.apiUrl.replace(/\/+$/, '');

  constructor(private http: HttpClient) { }

  private formatUrl(path: string) {
    return `${this.baseUrl}/${path.replace(/^\/+/, '')}`;
  }

  private toHttpParams(params?: ParamsLike): HttpParams | undefined {
    if (!params) return undefined;
    if (params instanceof HttpParams) return params;
    let p = new HttpParams();
    for (const key of Object.keys(params)) {
      const val = (params as any)[key];
      if (val === undefined || val === null) continue;
      if (Array.isArray(val)) {
        for (const v of val) p = p.append(key, String(v));
      } else {
        p = p.append(key, String(val));
      }
    }
    return p;
  }

  private buildOptions(opts?: RequestOptions) {
    const options: any = {};
    const httpParams = this.toHttpParams(opts?.params);
    if (httpParams) options.params = httpParams;
    if (opts?.headers) options.headers = opts.headers;
    // keep typed to pick the right HttpClient overload
    return options as { params?: HttpParams; headers?: HttpHeaders; observe?: 'body' };
  }

  get<T>(path: string, opts?: RequestOptions): Observable<T> {
    const options = this.buildOptions(opts);
    return this.http.get<T>(this.formatUrl(path), options).pipe(catchError(this.handleError));
  }

  post<T>(path: string, body: any, opts?: RequestOptions): Observable<T> {
    const options = this.buildOptions(opts);
    return this.http.post<T>(this.formatUrl(path), body, options).pipe(catchError(this.handleError));
  }

  put<T>(path: string, body: any, opts?: RequestOptions): Observable<T> {
    const options = this.buildOptions(opts);
    return this.http.put<T>(this.formatUrl(path), body, options).pipe(catchError(this.handleError));
  }

  delete<T>(path: string, opts?: RequestOptions): Observable<T> {
    const options = this.buildOptions(opts);
    return this.http.delete<T>(this.formatUrl(path), options).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    let message = 'An unknown error occurred';
    if (error.error instanceof ErrorEvent) {
      message = error.error.message;
    } else if (error.error && typeof error.error === 'string') {
      message = error.error;
    } else if (error.error && (error.error as any).message) {
      message = (error.error as any).message;
    } else if (error.status) {
      message = `Server returned code ${error.status}`;
    }
    return throwError(() => ({ status: error.status, message }));
  }
}
